'use client';

import { useEffect, useCallback, useRef } from 'react';

// ─────────────────────────────────────────────────────────────
// postMessage Bridge — Surfsterre ↔ Parent Page
//
// OUTBOUND (game → parent):
//   GAME_STARTED      { area: string, level: number }
//   OUTFIT_CHANGED    { outfitId: number, outfitName: string, isPaid: boolean }
//   OUTFIT_REQUESTED  { outfitId: number, outfitName: string, price: number }
//   PLAY_TRIGGERED    { timestamp: number }
//
// INBOUND (parent → game):
//   TOKEN_CHECK       { tokens: number, userId: string }
//   OUTFIT_UNLOCKED   { outfitId: number }
// ─────────────────────────────────────────────────────────────

export type OutboundEvent =
  | { type: 'GAME_STARTED';     area: string;       level: number }
  | { type: 'OUTFIT_CHANGED';   outfitId: number;   outfitName: string; isPaid: boolean }
  | { type: 'OUTFIT_REQUESTED'; outfitId: number;   outfitName: string; price: number }
  | { type: 'PLAY_TRIGGERED';   timestamp: number };

export type InboundEvent =
  | { type: 'TOKEN_CHECK';    tokens: number;  userId: string }
  | { type: 'OUTFIT_UNLOCKED'; outfitId: number };

interface BridgeHandlers {
  onTokenCheck?: (tokens: number, userId: string) => void;
  onOutfitUnlocked?: (outfitId: number) => void;
}

export function usePostMessageBridge(handlers: BridgeHandlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // ── Send a message to the parent frame
  const send = useCallback((event: OutboundEvent) => {
    if (typeof window === 'undefined') return;
    const target = window.parent !== window ? window.parent : window;
    try {
      target.postMessage({ source: 'surfsterre-game', ...event }, '*');
      console.log('[PostMessageBridge] ▶ sent:', event.type, event);
    } catch (err) {
      console.warn('[PostMessageBridge] Failed to send:', err);
    }
  }, []);

  // ── Listen for messages from the parent frame
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onMessage = (e: MessageEvent) => {
      const data = e.data as InboundEvent & { source?: string };
      if (!data || typeof data.type !== 'string') return;

      console.log('[PostMessageBridge] ◀ received:', data.type, data);

      switch (data.type) {
        case 'TOKEN_CHECK': {
          const { tokens, userId } = data as { type: 'TOKEN_CHECK'; tokens: number; userId: string };
          handlersRef.current.onTokenCheck?.(tokens, userId);
          break;
        }
        case 'OUTFIT_UNLOCKED': {
          const { outfitId } = data as { type: 'OUTFIT_UNLOCKED'; outfitId: number };
          handlersRef.current.onOutfitUnlocked?.(outfitId);
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return { send };
}
